import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight, Briefcase } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getDepartments, getJobPostsByDepartment } from "@/data/jobPosts";
import { useLanguage } from "@/hooks/useLanguage";

const CareersJobList = () => {
  const { t, localizePath, language } = useLanguage();
  const depts = getDepartments(language);
  const [selectedDepartment, setSelectedDepartment] = useState<string>(depts[0]);
  const filteredJobs = getJobPostsByDepartment(selectedDepartment, language);

  return (
    <section id="jobs" className="py-20 bg-background scroll-mt-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            {t("careers.jobList.title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("careers.jobList.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {depts.map((dept) => (
            <button
              key={dept}
              onClick={() => setSelectedDepartment(dept)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedDepartment === dept
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
              }`}
            >
              {dept}
            </button>
          ))}
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {filteredJobs.map((job) => (
            <Link key={job.slug} to={localizePath(`/karriere/${job.slug}`)}>
              <Card className="border-border/50 hover:shadow-lg hover:border-accent/30 transition-all group">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="bg-accent/10 text-accent">
                          {job.department}
                        </Badge>
                        {job.remote && (
                          <Badge variant="outline" className="text-muted-foreground">
                            {t("careers.jobDetail.remotePossible")}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground group-hover:text-accent transition-colors mb-2">
                        {job.title}
                      </h3>
                      
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                        {job.shortDescription}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Button variant="ghost" size="sm" className="group-hover:bg-accent/10 group-hover:text-accent">
                        {t("careers.jobList.details")}
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}

          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {t("careers.jobList.noJobs")}
              </p>
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto mt-12 text-center">
          <Card className="border-dashed border-2 border-border/50 bg-secondary/20">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t("careers.jobList.spontaneous")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("careers.jobList.spontaneousText")}
              </p>
              <Button variant="outline" asChild>
                <a href="mailto:jobs@chathero.ai">
                  {t("careers.jobList.spontaneousCta")}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CareersJobList;
